import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Repeat, Trash2, Bug } from "lucide-react";

const INTERVALS = ["weekly", "biweekly", "monthly"];

export const TurfLoops = ({ clients = [] }) => {
  const [loops, setLoops] = useState([]);
  const [varieties, setVarieties] = useState([]);
  const [form, setForm] = useState({ client_id: "", grass_type: "", service_interval: "weekly", client_phone: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/turf-loops").then((r) => setLoops(r.data));
  useEffect(() => { load(); api.get("/grass").then((r) => setVarieties(r.data)); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.client_id || !form.grass_type) { toast.error("Pick a client and grass variety"); return; }
    setBusy(true);
    try {
      await api.post("/turf-loops", form);
      toast.success("Service loop deployed");
      setForm({ client_id: "", grass_type: "", service_interval: "weekly", client_phone: "" });
      await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const rainCheck = async () => {
    try { await api.post("/turf-loops/rain-check"); toast.success("Rain check running — clients will be notified if rain is forecast"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const remove = async (l) => {
    try { await api.delete(`/turf-loops/${l.id}`); toast.success("Loop removed"); await load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div data-testid="turf-loops" className="rounded-2xl border border-black/5 bg-white p-6 shadow-[0_8px_32px_rgba(0,0,0,0.04)]">
      <h2 className="font-serif text-2xl text-emerald-950 flex items-center justify-between gap-2">
        <span className="flex items-center gap-2"><Repeat className="w-5 h-5 text-emerald-700" /> Turf Service Loops</span>
        <Button data-testid="loop-raincheck-btn" size="sm" variant="outline" onClick={rainCheck} className="rounded-full text-sm">Run rain check now</Button>
      </h2>
      <p className="mt-1 text-xs text-stone-400">Assign a grass variety + recurring cadence per client. Active threats auto-populate from the Turf Identifier. Heavy-rain days auto-flag a Rain Delay and notify the client (email + SMS).</p>

      <form onSubmit={create} className="mt-4 grid sm:grid-cols-5 gap-2">
        <select data-testid="loop-client" value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} className="rounded-xl border border-stone-200 h-10 px-3 text-sm">
          <option value="">Client…</option>
          {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select data-testid="loop-grass" value={form.grass_type} onChange={(e) => setForm({ ...form, grass_type: e.target.value })} className="rounded-xl border border-stone-200 h-10 px-3 text-sm">
          <option value="">Grass variety…</option>
          {varieties.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
        </select>
        <select data-testid="loop-interval" value={form.service_interval} onChange={(e) => setForm({ ...form, service_interval: e.target.value })} className="rounded-xl border border-stone-200 h-10 px-3 text-sm capitalize">
          {INTERVALS.map((i) => <option key={i} value={i}>{i}</option>)}
        </select>
        <input data-testid="loop-phone" value={form.client_phone} onChange={(e) => setForm({ ...form, client_phone: e.target.value })} placeholder="Client phone (SMS, optional)" className="rounded-xl border border-stone-200 h-10 px-3 text-sm" />
        <Button data-testid="loop-create-btn" type="submit" disabled={busy} className="rounded-full bg-emerald-900 hover:bg-emerald-800 text-white">Deploy loop</Button>
      </form>

      <div className="mt-5 space-y-2">
        {loops.length === 0 && <p className="text-sm text-stone-400">No service loops yet.</p>}
        {loops.map((l) => (
          <div key={l.id} data-testid={`loop-${l.id}`} className="rounded-xl border border-stone-100 bg-stone-50/60 p-4">
            <div className="flex items-start justify-between flex-wrap gap-2">
              <div>
                <p className="font-semibold text-emerald-950">{l.client_name} · {l.variety}</p>
                <p className="text-xs text-stone-500 capitalize">{l.service_interval} service · last inspected {new Date(l.last_inspection_date).toLocaleDateString()}</p>
                <div className="mt-2 flex flex-wrap gap-1">
                  {l.active_threats.map((t, i) => (
                    <Badge key={i} variant="outline" className="rounded-full text-amber-700 border-amber-200"><Bug className="w-3 h-3 mr-1" />{t}</Badge>
                  ))}
                </div>
              </div>
              <Button data-testid={`loop-remove-${l.id}`} size="sm" variant="outline" onClick={() => remove(l)} className="rounded-full text-red-600 border-red-200 hover:bg-red-50"><Trash2 className="w-4 h-4" /></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
